<template>
  <div>
    <sdPageHeader title="Nilai Kinerja Organisasi Entitas" />
    <Main>
      <!-- Filter Section -->
      <a-row :gutter="25" class="mb-25">
        <a-col :span="24">
          <sdCards headless>
            <a-row :gutter="16">
              <a-col :md="6" :sm="24">
                <a-select
                  placeholder="Pilih Bulan"
                  style="width: 100%"
                  v-model:value="filters.bulan"
                  @change="refreshData"
                >
                  <a-select-option v-for="bulan in bulanOptions" :key="bulan.value" :value="bulan.value">
                    {{ bulan.label }}
                  </a-select-option>
                </a-select>
              </a-col>
              <a-col :md="6" :sm="24">
                <a-select
                  placeholder="Pilih Tahun"
                  style="width: 100%"
                  v-model:value="filters.tahun"
                  @change="refreshData"
                >
                  <a-select-option v-for="tahun in tahunOptions" :key="tahun" :value="tahun">
                    {{ tahun }}
                  </a-select-option>
                </a-select>
              </a-col>
              <a-col :md="6" :sm="24">
                <a-select
                  placeholder="Pilih Entitas"
                  style="width: 100%"
                  v-model:value="filters.entitas"
                  @change="refreshData"
                >
                  <a-select-option v-for="entitas in options.entitas" :key="entitas.value" :value="entitas.value">
                    {{ entitas.text }}
                  </a-select-option>
                </a-select>
              </a-col>
              <a-col :md="6" :sm="24">
                <a-select
                  placeholder="Pilih Realisasi"
                  style="width: 100%"
                  v-model:value="filters.jenis_realisasi"
                  @change="refreshData"
                >
                  <a-select-option
                    v-for="jenis_realisasi in options.jenis_realisasi"
                    :key="jenis_realisasi.value"
                    :value="jenis_realisasi.value"
                  >
                    {{ jenis_realisasi.label }}
                  </a-select-option>
                </a-select>
              </a-col>
            </a-row>
          </sdCards>
        </a-col>
      </a-row>

      <!-- Gauge Chart Section -->
      <a-row :gutter="25">
        <a-col :xxl="8" :xl="8" :lg="12" :md="12" :sm="24" :xs="24">
          <RatioCard>
            <sdCards headless :title="entitasData.nama_entitas">
              <div v-if="isLoading" class="spin">
                <a-spin />
              </div>
              <div v-else>
                <div class="ratio-content">
                  <a-row :gutter="0">
                    <a-col :sm="12" :xs="12">
                      <a>
                        <sdHeading as="h1" class="big-number">{{ formatNumber(entitasData.total) }} %</sdHeading>
                      </a>
                    </a-col>
                    <a-col :sm="12" :xs="12">
                      <div :id="'entitas-gauge'" class="gauge-container"></div>
                    </a-col>
                  </a-row>
                </div>
              </div>
            </sdCards>
          </RatioCard>
        </a-col>
        <a-col :xxl="16" :xl="16" :lg="12" :md="12" :sm="24" :xs="24">
          <sdCards title="Data Entitas" bodypadding="0px">
            <a-table
              :columns="entitasColumns"
              :data-source="dataSource"
              :pagination="false"
              :loading="isLoading"
              row-key="key"
            >
            </a-table>
          </sdCards>
        </a-col>
      </a-row>

      <!-- Pencapaian Bulan Section -->
      <a-row :gutter="25" class="mt-25">
        <a-col :span="24">
          <sdCards
            :title="
              'Pencapaian Bulan ' + (filters.bulan && months[filters.bulan - 1] ? months[filters.bulan - 1].full : '')
            "
            bodypadding="0px"
          >
            <a-table
              :columns="indicatorColumns"
              :data-source="dataSource2"
              :row-class-name="rowClassName"
              :pagination="false"
              :loading="isLoading"
            >
            </a-table>
          </sdCards>
        </a-col>
      </a-row>

      <!-- Refresh Data Button -->
      <a-row :gutter="25" class="mt-25">
        <a-col :span="24" style="text-align: right;">
          <a-button type="primary" @click="refreshData">Refresh Data</a-button>
        </a-col>
      </a-row>
    </Main>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed, h } from 'vue';

import { Main } from '@/view/styled';
import { RatioCard } from '@/view/dashboard/style';

let GaugeChart = require('vue-gauge/assets/bundle.js');
import axiosApi from '@/api';
import { getItem, setItem } from '@/utility/localStorageControl';
import { MONTH_OPTIONS } from '@/constants/monthOptions';
import formatNumber from '@/utility/formatNumber';
import { renderAvatar } from '@/utility/renderComponents';
import { getStyleFlags } from '@/utility/rekap/getStyleFlags';
import { polarisasiIcon } from '@/utility/polarisasiUtils';

export default {
  name: 'NkoEntitas',
  components: {
    Main,
    RatioCard,
  },
  setup() {
    const accessToken = getItem('access_token');
    const entitas = getItem('entitas');
    const tahun = getItem('tahun');
    const bulan = getItem('bulan');
    const isLoading = ref(false);
    const gaugeInstances = ref({});

    // Filter options
    const bulanOptions = MONTH_OPTIONS;

    const { months } = require('@/utility/monthsData');

    const currentYear = new Date().getFullYear();
    const tahunOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

    // No need for entitasOptions as we're using options.entitas

    // Using the formatNumber utility function imported from @/utility/formatNumber
    const filters = reactive({
      bulan: bulan ? bulan : new Date().getMonth() + 1,
      tahun: tahun ? tahun : currentYear,
      entitas: entitas ? entitas : null,
      jenis_realisasi: 'Self Assessment',
    });

    const options = reactive({
      entitas: [],
      jenis_realisasi: [
        { value: 'Self Assessment', label: 'Self Assessment' },
        { value: 'Verifikasi', label: 'Verifikasi' },
      ],
    });

    // Gauge chart options
    const gaugeOptions = reactive({
      needleValue: 0,
      hasNeedle: true,
      needleColor: 'rgba(0, 0, 0, 0.8)',
      arcDelimiters: [30, 50, 70, 95, 99],
      arcColors: ['#f20f0f', '#f74848', '#f76868', '#fa8484', '#eeff59', '#008000'],
      rangeLabel: ['0', '100'],
    });

    // Table columns for entitas
    const entitasColumns = [
      {
        title: 'No',
        dataIndex: 'number',
        key: 'number',
        width: 60,
      },
      {
        title: 'Nama Entitas',
        dataIndex: 'nama_entitas',
        key: 'nama_entitas',
      },
      {
        title: 'Jumlah Indikator',
        dataIndex: 'jumlah_indikator',
        key: 'jumlah_indikator',
        width: 150,
        align: 'center',
      },
      {
        title: 'Hijau',
        dataIndex: 'jumlah_hijau',
        key: 'jumlah_hijau',
        width: 100,
        align: 'center',
      },
      {
        title: 'Kuning',
        dataIndex: 'jumlah_kuning',
        key: 'jumlah_kuning',
        width: 100,
        align: 'center',
      },
      {
        title: 'Merah',
        dataIndex: 'jumlah_merah',
        key: 'jumlah_merah',
        width: 100,
        align: 'center',
      },
      {
        title: 'Belum Dinilai',
        dataIndex: 'belum_dinilai',
        key: 'belum_dinilai',
        width: 120,
        align: 'center',
      },
      {
        title: 'NKO',
        dataIndex: 'nko',
        key: 'nko',
        width: 100,
        align: 'center',
      },
    ];

    // Sample data for entitas table
    const entitasData = ref([]);

    const entitasList = ref([]);

    const indicatorData = ref([]);

    // Table columns for indicators
    const indicatorColumns = reactive([
      {
        title: 'No',
        dataIndex: 'number',
        key: 'number',
        //width: 60
      },
      {
        title: 'Indikator',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Polaritas',
        // dataIndex: 'polarisasi',
        key: 'polarisasi',
        width: 150,
        align: 'center',
        customRender: ({ record }) => {
          return polarisasiIcon(record.polarisasi);
        },
      },
      {
        title: 'Satuan',
        dataIndex: 'satuan',
        key: 'satuan',
        width: 150,
        align: 'center',
      },
      {
        title: 'Bobot',
        dataIndex: 'bobot',
        key: 'bobot',
        align: 'center',
        width: 150,
      },
      {
        title: 'Target KM',
        dataIndex: 'target_tahunan',
        key: 'target_tahunan',
        align: 'center',
        width: 150,
        customRender: ({ text }) => {
          return h('div', { style: { whiteSpace: 'pre-line' } }, text);
        },
      },
      {
        title: computed(() => 'Target Bulan ' + months[filters.bulan - 1].short),
        dataIndex: 'target',
        key: 'target',
        align: 'center',
        width: 150,
        customRender: ({ text }) => {
          return h('div', { style: { whiteSpace: 'pre-line' } }, text);
        },
        customCell: record => {
          if (record.is_parent) {
            return {
              style: {
                backgroundColor: '#FADA7A',
              },
            };
          }
          return {
            style: {
              backgroundColor: !record.target && !record.is_parent ? '#f5f5f5' : '#FFFFFF',
            },
          };
        },
      },
      {
        title: 'Pencapaian',
        dataIndex: 'pencapaian',
        key: 'pencapaian',
        align: 'center',
        width: 150,
      },
      {
        title: 'Nilai',
        dataIndex: 'nilai',
        key: 'nilai',
        align: 'center',
        width: 150,
      },
      {
        title: 'Ket',
        //dataIndex: 'keterangan',
        key: 'keterangan',
        align: 'center',
        width: 150,
        customRender: ({ record }) => {
          return renderAvatar(record.keterangan);
        },
      },
    ]);

    const dataSource = computed(() => {
      if (entitasList.value.length == 0) return [];
      return entitasList.value.map((item, index) => {
        return {
          number: index + 1,
          nama_entitas: item.nama_entitas,
          jumlah_indikator: item.jumlah_indikator,
          jumlah_hijau: item.jhijau,
          jumlah_kuning: item.jkuning,
          jumlah_merah: item.jmerah,
          belum_dinilai: item.jumlah_indikator - (item.jhijau + item.jkuning + item.jmerah),
          nko: formatNumber(item.nko),
          key: item.id || `item-${index + 1}`, // Use item.id if available, otherwise generate a unique key
        };
      });
    });

    // Main computed property for indicator data
    const dataSource2 = computed(() => {
      if (!indicatorData.value?.length) return [];

      return indicatorData.value.map((item, index) => {
        // Create status indicator if color was determined
        const { keterangan } = getStyleFlags(item, item.jenis_variable_kpi_id == 3, item.name == 'NKO');

        // Return formatted row data
        return {
          number: item.number,
          is_parent: Boolean(!item.parent_id),
          name: item.name,
          polarisasi: item.polarisasi,
          satuan: item.satuan_kpi?.name || '',
          bobot: item.bobot,
          target_tahunan: formatNumber(item.target_km),
          target: formatNumber(item.target),
          realisasi: formatNumber(item.realisasi),
          pencapaian: item.pencapaian ? `${formatNumber(item.pencapaian)} %` : null,
          nilai: formatNumber(item.nilai),
          keterangan: keterangan,
          key: item.id || `item-${index + 1}`, // Use item.id if available, otherwise generate a unique key
        };
      });
    });

    // Function to view detail
    const viewDetail = record => {
      console.log('View detail for:', record);
      // Implement navigation or modal for detail view
    };

    // Function to refresh data
    const refreshData = () => {
      getData(filters.entitas, filters.bulan, filters.tahun, filters.jenis_realisasi);
      setItem('entitas', filters.entitas);
      setItem('tahun', filters.tahun);
      setItem('bulan', filters.bulan);
    };

    onMounted(() => {
      setTimeout(() => {
        const gaugeId = 'entitas-gauge';
        const element = document.getElementById(gaugeId);

        if (element) {
          // Clear the element first to prevent duplicates
          element.innerHTML = '';

          // Create new gauge instance
          const gauge = GaugeChart.gaugeChart(element, 250, gaugeOptions);

          // Store the instance and update needle
          gaugeInstances.value[gaugeId] = gauge;
          gauge.updateNeedle(entitasData.value.total ?? 0);
        }
      }, 100);

      axiosApi.defaults.headers.common['Authorization'] = 'Bearer ' + accessToken;
      axiosApi
        .get('options-entitas')
        .then(response => {
          options.entitas = response.data;
        })
        .catch(error => {
          console.error('Error fetching entitas options:', error);
          console.error('Gagal mengambil data entitas');
        });

      if (tahun && bulan && entitas) {
        getData(entitas, bulan, tahun, filters.jenis_realisasi);
      }
    });

    const getData = (id, bulan, tahun, jenis_realisasi) => {
      isLoading.value = true;
      axiosApi.defaults.headers.common['Authorization'] = 'Bearer ' + accessToken;
      axiosApi
        .get('nko-laporan', {
          params: {
            entitas_id: id,
            month: bulan,
            year: tahun,
            jenis_realisasi: jenis_realisasi,
          },
        })
        .then(response => {
          if (response.data) {
            entitasData.value = response.data.entitas;
            indicatorData.value = response.data.data_kpi;
            entitasList.value = response.data.daftar_entitas;

            // Update gauge with new data
            setTimeout(() => {
              const gaugeId = 'entitas-gauge';
              const element = document.getElementById(gaugeId);

              if (element) {
                // Clear the element first to prevent duplicates
                element.innerHTML = '';

                // Create new gauge instance
                const gauge = GaugeChart.gaugeChart(element, 250, gaugeOptions);

                // Store the instance and update needle
                gaugeInstances.value[gaugeId] = gauge;
                gauge.updateNeedle(entitasData.value.total ?? 0);
              }
            }, 100);
          }
          isLoading.value = false;
        })
        .catch(() => {
          console.error('Gagal mengambil data indikator');
          isLoading.value = false;
        });
    };

    const rowClassName = record => {
      if (record.is_parent) {
        return 'table-row-minor';
      } else {
        return '';
      }
    };

    return {
      filters,
      bulanOptions,
      tahunOptions,
      options,
      gaugeOptions,
      entitasColumns,
      entitasData,
      indicatorColumns,
      indicatorData,
      viewDetail,
      refreshData,
      getData,
      dataSource,
      dataSource2,
      entitasList,
      isLoading,
      months,
      rowClassName,
      formatNumber,
      renderAvatar,
    };
  },
};
</script>
<style scoped>
.custom-icon {
  margin-left: 10px;
  color: rgb(54, 61, 68);
}

::v-deep(.table-row-minor) {
  background-color: #fada7a;
  font-weight: bold;
  font-size: 14px;
}

::v-deep .ant-table-thead > tr > th {
  background-color: #578fca; /* navy */
  color: white;
}
</style>
